import { useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import hmrcShield from '../assets/landing/hmrc-shield.png'
import avatar1 from '../assets/landing/avatar-1.jpg'
import avatar2 from '../assets/landing/avatar-2.png'
import NavHeader from './NavHeader'

export default function HeroSection() {
  const navigate = useNavigate()

  return (
    <>
      <NavHeader />

      {/* Hero Section */}
      <section className='relative w-full bg-white overflow-hidden text-left'>
        <div className='relative mx-auto max-w-[1920px] px-4 sm:px-6 lg:px-20 pt-8 sm:pt-12 lg:pt-16 xl:pt-[82px] pb-12 sm:pb-16 lg:pb-20 text-left'>
          {/* HMRC shield wrapper (desktop) — big faint version sits behind the
              small sharp version, mirroring the Figma "Image 60" + "Image 59"
              double-exposure treatment. The source asset has whitespace
              around the shield, so we recreate Figma's crop by scaling the
              image up to ~298% inside a fixed container and clipping. */}
          <div className='hidden lg:block absolute right-4 sm:right-6 lg:right-20 top-2 xl:top-8 2xl:top-[60px] pointer-events-none select-none'>
            <div className='relative w-[340px] xl:w-[420px] 2xl:w-[560px] h-[400px] xl:h-[500px] 2xl:h-[665px]'>
              {/* Big faint shield (cropped) — anchored top-right */}
              <div className='absolute inset-0 overflow-hidden opacity-[0.08]'>
                <img
                  src={hmrcShield}
                  alt=''
                  aria-hidden='true'
                  className='absolute max-w-none'
                  style={{
                    width: '298%',
                    height: '251%',
                    left: '-95.44%',
                    top: '-71.33%',
                  }}
                />
              </div>
              {/* Sharp shield (cropped) — offset toward lower-left */}
              <div
                className='absolute overflow-hidden'
                style={{
                  left: '-7%',
                  top: '27%',
                  width: '53%',
                  height: '44%',
                }}
              >
                <img
                  src={hmrcShield}
                  alt='HMRC Recognised for Corporation Tax Filing'
                  className='absolute max-w-none'
                  style={{
                    width: '298%',
                    height: '251%',
                    left: '-95.44%',
                    top: '-71.33%',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Mobile / tablet HMRC shield (cropped to remove whitespace) */}
          <div className='lg:hidden flex justify-center mb-6'>
            <div className='relative w-[220px] sm:w-[260px] h-[260px] sm:h-[308px] overflow-hidden'>
              <img
                src={hmrcShield}
                alt='HMRC Recognised for Corporation Tax Filing'
                className='absolute max-w-none'
                style={{
                  width: '298%',
                  height: '251%',
                  left: '-95.44%',
                  top: '-71.33%',
                }}
              />
            </div>
          </div>

          {/* Hero text column */}
          <div className='relative z-10 flex flex-col items-start gap-6 sm:gap-7 lg:gap-[30px] max-w-full lg:max-w-[calc(100%-360px)] xl:max-w-[calc(100%-440px)] 2xl:max-w-[1021px] text-left'>
            {/* Tag pill */}
            <div
              className='bg-[#E5E8EB]/80 rounded-[24px] px-5 sm:px-7 py-2 sm:py-2.5'
              style={{ fontFamily: '"DM Sans", sans-serif' }}
            >
              <p className='text-[#0D0D0F] font-bold text-[13px] sm:text-[15px] leading-6 whitespace-nowrap text-left'>
                Simplify Your UK Tax Filing in Minutes
              </p>
            </div>

            {/* Heading with avatar group inline */}
            <h1
              className='w-full text-left text-[#001B38] font-bold leading-[1.12] tracking-[-0.5px] sm:tracking-[-1px] lg:tracking-[-1.6px] xl:tracking-[-2.4px] text-[34px] sm:text-[44px] md:text-[54px] lg:text-[64px] xl:text-[76px] 2xl:text-[85px]'
              style={{ fontFamily: '"DM Sans", sans-serif' }}
            >
              File Your Taxes and Company Returns with Confidence{' '}
              <span className='inline-flex items-center align-middle ml-1 sm:ml-2 lg:ml-3'>
                <AvatarGroup />
              </span>
            </h1>

            {/* Description */}
            <p
              className='w-full text-left text-[#535862] font-medium text-base sm:text-[17px] lg:text-[18px] leading-7 max-w-full lg:max-w-[743px]'
              style={{ fontFamily: '"DM Sans", sans-serif' }}
            >
              BritFiler is an HMRC recognised one-stop filing software designed
              to help UK businesses file Corporation Tax, Self Assessment,
              Annual Accounts and Confirmation Statements — all in one place.
              Simple, fast, and Companies House compliant.
            </p>

            {/* CTA button */}
            <button
              onClick={() => navigate('/sign-up')}
              className='group relative inline-flex items-center justify-between bg-[#001B38] text-white rounded-[100px] pl-8 sm:pl-10 pr-2 h-[52px] sm:h-[57px] w-full max-w-[316px] hover:bg-[#002a50] transition-colors cursor-pointer'
              style={{ fontFamily: '"DM Sans", sans-serif' }}
            >
              <span className='text-base font-semibold leading-6 text-left'>
                Get Started For Free
              </span>
              <span className='flex items-center justify-center w-[37px] h-[37px] bg-[#F04438] rounded-full ml-3 transition-transform group-hover:translate-x-1'>
                <ArrowRight className='w-[18px] h-[18px] text-white' strokeWidth={2.5} />
              </span>
            </button>
          </div>
        </div>
      </section>
    </>
  )
}

function AvatarGroup() {
  return (
    <span className='inline-flex items-center -space-x-3 sm:-space-x-4'>
      <span className='inline-block w-[34px] h-[34px] sm:w-[44px] sm:h-[44px] lg:w-[52px] lg:h-[52px] xl:w-[61px] xl:h-[61px] rounded-full ring-2 ring-white overflow-hidden bg-[#E6E6E6]'>
        <img
          src={avatar1}
          alt=''
          className='w-full h-full object-cover'
        />
      </span>
      <span className='inline-block w-[34px] h-[34px] sm:w-[44px] sm:h-[44px] lg:w-[52px] lg:h-[52px] xl:w-[61px] xl:h-[61px] rounded-full ring-2 ring-white overflow-hidden bg-[#D5CCC3]'>
        <img
          src={avatar2}
          alt=''
          className='w-full h-full object-cover'
        />
      </span>
      <span
        className='inline-flex items-center justify-center w-[34px] h-[34px] sm:w-[44px] sm:h-[44px] lg:w-[52px] lg:h-[52px] xl:w-[61px] xl:h-[61px] rounded-full ring-2 ring-white bg-[#001B38] text-[#F6F6F6] font-normal text-xs sm:text-base lg:text-lg xl:text-[21.4px]'
        style={{ fontFamily: '"Arial Narrow", Arial, sans-serif' }}
      >
        2+
      </span>
    </span>
  )
}
